import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { AdminSubNav, CHATBOT_TABS } from "@/components/admin/AdminSubNav";
import {
  listChatConversations,
  getChatTranscript,
  type ChatConversationRow,
  type ChatMessageRow,
} from "@/lib/chat-admin.functions";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/admin/chatbot/conversations")({
  head: () => ({
    meta: [
      { title: "Conversations · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ChatbotConversations,
});

function ChatbotConversations() {
  const listFn = useServerFn(listChatConversations);
  const transcriptFn = useServerFn(getChatTranscript);
  const [rows, setRows] = useState<ChatConversationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<ChatMessageRow[] | null>(null);

  useEffect(() => {
    listFn()
      .then(setRows)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [listFn]);

  useEffect(() => {
    if (!activeId) return;
    setTranscript(null);
    transcriptFn({ data: { conversationId: activeId } })
      .then(setTranscript)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [activeId, transcriptFn]);

  return (
    <div>
      <AdminSubNav tabs={CHATBOT_TABS} />
      <h1 className="text-xl font-black">Conversations</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Every chat visitors have with the assistant on the public site.
      </p>

      {error ? <p className="mt-4 text-sm text-destructive">{error}</p> : null}

      <div className="mt-6 grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="rounded-xl border border-border">
          {rows === null ? (
            <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </p>
          ) : rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">No conversations yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setActiveId(r.id)}
                    className={`w-full px-4 py-3 text-left text-sm transition ${
                      activeId === r.id ? "bg-brand text-white" : "hover:bg-muted"
                    }`}
                  >
                    <span className="block font-semibold">{r.source_page || "Site chat"}</span>
                    <span className="block text-xs opacity-75">
                      {new Date(r.updated_at).toLocaleString()} · {r.message_count} messages
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="min-h-[240px] rounded-xl border border-border p-4">
          {!activeId ? (
            <p className="text-sm text-muted-foreground">Select a conversation to read it.</p>
          ) : transcript === null ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading transcript…
            </p>
          ) : (
            <div className="space-y-3">
              {transcript.map((m) => (
                <div key={m.id}>
                  <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
                    {m.role === "user" ? "Visitor" : "Assistant"}
                  </p>
                  <p className="whitespace-pre-wrap text-sm">{m.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
